import { useEffect, useState } from 'react';
import { api, type PublicMovieListing } from '../lib/api';
import './MovieLibrary.css';

export default function MovieLibrary({
  queuedVideoIds,
  nowPlayingVideoId,
  onRequest,
}: {
  queuedVideoIds: string[];
  nowPlayingVideoId?: string | null;
  onRequest: (youtubeUrl: string) => Promise<void>;
}) {
  const [query, setQuery] = useState('');
  const [movies, setMovies] = useState<PublicMovieListing[]>([]);
  const [total, setTotal] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queued = new Set(queuedVideoIds);

  async function loadList(nextQuery = query, shuffle = !nextQuery.trim()) {
    setError(null);
    setLoading(true);
    try {
      const res = await api.movies({
        q: nextQuery.trim() || undefined,
        shuffle,
        limit: nextQuery.trim() ? 48 : 24,
      });
      setMovies(res.movies);
      setTotal(res.total);
    } catch (err) {
      setError((err as Error).message || 'Could not load public domain movies.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const needle = query.trim();
    const timer = window.setTimeout(() => {
      void loadList(needle, !needle);
    }, needle ? 350 : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function requestMovie(movie: PublicMovieListing) {
    setError(null);
    setBusyId(movie.youtube_video_id);
    try {
      await onRequest(movie.youtube_url);
    } catch (err) {
      setError((err as Error).message || 'That movie could not be added right now.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="movie-library panel fade-up">
      <div className="movie-library-head">
        <div>
          <h2 className="section-title">PUBLIC DOMAIN MOVIES</h2>
          <p className="muted movie-library-copy">
            These films and shows are in the public domain and free to watch on YouTube. Shuffle for
            a new batch, or paste a YouTube URL in the section above.
          </p>
        </div>
        <div className="movie-library-tools">
          <button
            type="button"
            className="btn btn-gold"
            onClick={() => {
              if (query) setQuery('');
              else void loadList('', true);
            }}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Shuffle list'}
          </button>
          <input
            className="field movie-library-search"
            type="search"
            placeholder="Search public domain movies..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search public domain movies"
          />
        </div>
      </div>

      <p className="muted movie-library-count">
        {loading
          ? 'Loading titles…'
          : `Showing ${movies.length} of ${total.toLocaleString()} titles`}
      </p>

      {error && <div className="error-banner">{error}</div>}

      {!loading && movies.length === 0 ? (
        <p className="muted">No titles match that search.</p>
      ) : (
        <div className="movie-library-grid">
          {movies.map((movie) => {
            const isPlaying = nowPlayingVideoId === movie.youtube_video_id;
            const isQueued = queued.has(movie.youtube_video_id);
            const busy = busyId === movie.youtube_video_id;
            return (
              <article key={movie.youtube_video_id} className="movie-card">
                <img className="movie-card-thumb" src={movie.poster_url} alt="" />
                <div className="movie-card-body">
                  <h3 className="movie-card-title">{movie.title}</h3>
                  <p className="movie-card-meta">
                    {[movie.year, movie.genre].filter(Boolean).join(' · ')}
                  </p>
                  {movie.blurb && <p className="muted movie-card-blurb">{movie.blurb}</p>}
                  <button
                    className="btn btn-primary"
                    disabled={busy || isPlaying || isQueued}
                    onClick={() => void requestMovie(movie)}
                  >
                    {isPlaying ? 'Playing now' : isQueued ? 'In queue' : busy ? 'Requesting…' : 'Request'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
